import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatMoney } from "@/lib/currencyFormat";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InvoiceLineCatalogPicker } from "@/components/features/invoices/InvoiceLineCatalogPicker";
import {
  getLineTaxAmount,
  isPriceWithoutVat,
  TAX_RATE_BY_CATEGORY,
} from "@/lib/invoice-line-tax";
import type { ClientCatalogItem } from "@/services/clientCatalogService";
import type { TaxCategory } from "@fiscalo/shared-types";

export interface RecurringLineForm {
  description: string;
  quantity: number;
  unitPrice: number;
  taxCategory: TaxCategory;
  taxAmount: number;
  catalogItemId?: string | null;
}

export interface RecurringInvoiceLinesCardProps {
  linesForm: RecurringLineForm[];
  updateLine: (index: number, patch: Partial<RecurringLineForm>) => void;
  addLine: () => void;
  removeLine: (index: number) => void;
  cfeType: "101" | "111" | "121";
  currency: "UYU" | "USD";
  onCurrencyChange: (v: "UYU" | "USD") => void;
  /** When set and non-empty, each line can pick from this client's catalog or type manually. */
  clientCatalogItems?: ClientCatalogItem[];
  tenantExoneratedRegimeEnabled?: boolean;
  tenantExoneratedRegimeLegend?: string | null;
}

export function RecurringInvoiceLinesCard({
  linesForm,
  updateLine,
  addLine,
  removeLine,
  cfeType,
  currency,
  onCurrencyChange,
  clientCatalogItems = [],
  tenantExoneratedRegimeEnabled,
  tenantExoneratedRegimeLegend,
}: RecurringInvoiceLinesCardProps): JSX.Element {
  const { t } = useTranslation();

  const applyCatalogItemToLine = (index: number, item: ClientCatalogItem): void => {
    const tax: TaxCategory =
      cfeType === "121"
        ? "export"
        : ((item.defaultTaxCategory as TaxCategory) || "vat_basic");
    updateLine(index, {
      description: item.description,
      unitPrice: item.unitPrice,
      taxCategory: tax,
      catalogItemId: item.id,
    });
  };

  const displayCurrency = cfeType === "121" ? "USD" : currency;

  const totals = useMemo(() => {
    const subtotal = linesForm.reduce(
      (s, l) => s + l.quantity * l.unitPrice,
      0
    );
    const taxTotal =
      cfeType === "121"
        ? 0
        : linesForm.reduce((s, l) => s + l.taxAmount, 0);
    return {
      subtotal,
      taxTotal,
      total: subtotal + taxTotal,
    };
  }, [linesForm, cfeType]);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
        <CardTitle className="text-foreground">{t("invoices.new.lines")}</CardTitle>
        <div className="flex flex-wrap items-center gap-3">
          {(cfeType === "101" || cfeType === "111") && (
            <div className="flex items-center gap-2">
              <Label
                htmlFor="recurring-line-currency"
                className="text-sm font-normal text-muted-foreground whitespace-nowrap"
              >
                {t("invoices.new.currency")}
              </Label>
              <Select
                value={currency}
                onValueChange={(v) => onCurrencyChange(v as "UYU" | "USD")}
              >
                <SelectTrigger id="recurring-line-currency" className="w-[100px] transition-colors duration-150">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UYU">{t("invoices.new.currencyUYU")}</SelectItem>
                  <SelectItem value="USD">{t("invoices.new.currencyUSD")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {tenantExoneratedRegimeEnabled && tenantExoneratedRegimeLegend?.trim() && (
          <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2">
            <p className="text-xs font-medium text-muted-foreground">
              {t("invoices.new.regimeLegendHint")}
            </p>
            <p className="mt-1 text-sm text-foreground">
              {tenantExoneratedRegimeLegend}
            </p>
          </div>
        )}
        {linesForm.map((line, index) => {
          const rate = TAX_RATE_BY_CATEGORY[line.taxCategory] ?? 0;
          const priceWithoutVat = isPriceWithoutVat(line.taxCategory);
          const lineTotalWithVat = line.quantity * line.unitPrice * (1 + rate);
          return (
            <div
              key={index}
              className="flex w-full flex-wrap items-end gap-3 rounded-lg border p-3"
            >
              <div className="min-w-[10rem] flex-1 space-y-2">
                {clientCatalogItems.length > 0 && (
                  <InvoiceLineCatalogPicker
                    items={clientCatalogItems}
                    selectedCatalogItemId={line.catalogItemId ?? null}
                    onSelectItem={(item) => applyCatalogItemToLine(index, item)}
                    onClearSelection={() => {
                      const defaultTax: TaxCategory =
                        cfeType === "121" ? "export" : "vat_basic";
                      updateLine(index, {
                        description: "",
                        quantity: 1,
                        unitPrice: 0,
                        taxCategory: defaultTax,
                        catalogItemId: null,
                      });
                    }}
                  />
                )}
                <div className="space-y-1">
                <Label className="text-xs whitespace-nowrap">
                  {t("invoices.new.description")}
                </Label>
                <Input
                  value={line.description}
                  onChange={(e) =>
                    updateLine(index, {
                      description: e.target.value,
                      catalogItemId: null,
                    })
                  }
                  placeholder={t("invoices.new.descriptionPlaceholder")}
                />
                </div>
              </div>
              <div className="w-24 shrink-0 space-y-1">
                <Label className="text-xs whitespace-nowrap">
                  {t("invoices.new.quantity")}
                </Label>
                <Input
                  type="number"
                  min={0.001}
                  step="any"
                  value={line.quantity || ""}
                  onChange={(e) =>
                    updateLine(index, {
                      quantity: Number(e.target.value) || 0,
                    })
                  }
                />
              </div>
              {cfeType !== "121" && (
                <div className="w-[150px] shrink-0 space-y-1">
                  <Label className="text-xs whitespace-nowrap">
                    {t("invoices.new.tasaIva")}
                  </Label>
                  <Select
                    value={line.taxCategory}
                    onValueChange={(v) =>
                      updateLine(index, {
                        taxCategory: v as TaxCategory,
                      })
                    }
                  >
                    <SelectTrigger className="transition-colors duration-150">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exempt">
                        {t("invoices.new.taxOptionExempt")}
                      </SelectItem>
                      <SelectItem value="vat_basic">
                        {t("invoices.new.taxOptionVatBasic")}
                      </SelectItem>
                      <SelectItem value="vat_minimum">
                        {t("invoices.new.taxOptionVatMinimum")}
                      </SelectItem>
                      <SelectItem value="other">
                        {t("invoices.new.taxOptionNotTaxable")}
                      </SelectItem>
                      <SelectItem value="export">
                        {t("invoices.new.taxOptionExport")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="w-32 shrink-0 space-y-1">
                <Label className="text-xs whitespace-nowrap">
                  {t("invoices.new.unitPriceWithoutVat")}
                </Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={line.unitPrice || ""}
                  onChange={(e) =>
                    updateLine(index, {
                      unitPrice: Number(e.target.value) || 0,
                    })
                  }
                />
              </div>
              {cfeType !== "121" && !priceWithoutVat && (
                <div className="w-32 shrink-0 space-y-1">
                  <Label className="text-xs whitespace-nowrap">
                    {t("invoices.new.amountWithVat")}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={lineTotalWithVat.toFixed(2)}
                    readOnly
                    className="bg-muted"
                  />
                </div>
              )}
              {linesForm.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeLine(index)}
                  className="ml-auto text-destructive hover:bg-destructive/10 hover:text-destructive transition-colors duration-150"
                >
                  {t("invoices.new.removeLine")}
                </Button>
              )}
            </div>
          );
        })}
        <div className="flex justify-start pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addLine}
            className="transition-colors duration-150"
          >
            {t("invoices.new.addLine")}
          </Button>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
          <div className="flex flex-wrap gap-4 text-sm font-medium justify-end">
            <span>
              {t("invoices.create.subtotal")}: {displayCurrency}{" "}
              {formatMoney(totals.subtotal)}
            </span>
            <span>
              {t("invoices.create.tax")}: {displayCurrency}{" "}
              {formatMoney(totals.taxTotal)}
            </span>
            <span>
              {t("invoices.create.total")}: {displayCurrency}{" "}
              {formatMoney(totals.total)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function createEmptyRecurringLine(
  taxCategory: TaxCategory = "vat_basic"
): RecurringLineForm {
  return {
    description: "",
    quantity: 1,
    unitPrice: 0,
    taxCategory,
    taxAmount: getLineTaxAmount(taxCategory, 1, 0),
  };
}
